import { useMemo } from "react";
import {
  type GridColumnProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { stripAriaSelected } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";
import { joinClasses } from "@/utils/styling";
import { Revision } from "../Tabs/RevisionsTab";

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

function formatTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

function ActivityDateCell(props: GridCustomCellProps) {
  const value = props.dataItem.activityDate as string;
  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <div className="fw-semibold" style={{ fontSize: "1rem" }}>
        {formatDate(value)}
      </div>
      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        {formatTime(value)}
      </div>
    </td>
  );
}

function RevisionCreateDateCell(props: GridCustomCellProps) {
  const value = props.dataItem.revisionCreateDate as string;
  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <div className="fw-semibold" style={{ fontSize: "1rem" }}>
        {formatDate(value)}
      </div>
      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        {formatTime(value)}
      </div>
    </td>
  );
}

function StatusCell(props: GridCustomCellProps) {
  const status: string = props.dataItem.revisionStatus;

  if (!status) return <td {...stripAriaSelected(props.tdProps)} />;

  const styleMap: Record<
    string,
    { bg: string; color: string; border: string }
  > = {
    Locked: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" },
    Certified: { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
    Uncertified: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
    Decertified: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
  };

  const s = styleMap[status] ?? {
    bg: "#f3f4f6",
    color: "#374151",
    border: "#e5e7eb",
  };

  return (
    <td {...stripAriaSelected(props.tdProps)}>
      <span
        className="rounded-pill px-3 py-1"
        style={{
          backgroundColor: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
          fontSize: "0.85rem",
          fontWeight: 600,
        }}
      >
        {status}
      </span>
    </td>
  );
}

function TotalRecordsCell(props: GridCustomCellProps) {
  const totalRecords = props.dataItem.totalRecords ?? 0;
  const cleanTdProps = stripAriaSelected(props.tdProps);
  return (
    <td {...cleanTdProps} className={joinClasses("text-center", cleanTdProps?.className)}>
      <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
        {totalRecords}
      </span>
    </td>
  );
}

function TotalErrorsCell(props: GridCustomCellProps) {
  const errors: number = props.dataItem.totalErrors ?? 0;
  const hasErrors = errors > 0;
  const cleanTdProps = stripAriaSelected(props.tdProps);

  return (
    <td {...cleanTdProps} className={joinClasses("text-center", cleanTdProps?.className)}>
      <span
        className="fw-bold d-inline-block px-2 py-1"
        style={{
          backgroundColor: hasErrors ? "#fee2e2" : "#d1fae5",
          color: hasErrors ? "#dc2626" : "#065f46",
          fontSize: "0.9rem",
          minWidth: "36px",
          textAlign: "center",
          borderRadius: "8px",
        }}
      >
        {errors}
      </span>
    </td>
  );
}

const columns: GridColumnProps[] = [
  {
    field: "activityDate",
    title: "Activity Date",
    cells: { data: ActivityDateCell },
  },
  {
    field: "revisionCreateDate",
    title: "Revision Create Date",
    cells: { data: RevisionCreateDateCell },
  },
  {
    field: "revisionStatus",
    title: "Revision Status",
    cells: { data: StatusCell },
  },
  {
    field: "totalRecords",
    title: "Total Records",
    headerClassName: "text-center",
    cells: { data: TotalRecordsCell },
  },
  {
    field: "totalErrors",
    title: "Total Errors",
    headerClassName: "text-center",
    cells: { data: TotalErrorsCell },
  },
];

const scopedStyles = `
  .revision-history-grid .k-grid {
    border: none !important;
  }
  .revision-history-grid .k-grid-header,
  .revision-history-grid .k-grid-header-wrap,
  .revision-history-grid .k-grid-header table {
    background-color: #f8fafc !important;
    border: none !important;
  }
  .revision-history-grid .k-grid-header-wrap {
    border-bottom: 1px solid #e2e8f0 !important;
  }
  .revision-history-grid .k-grid-header th.k-header,
  .revision-history-grid .k-grid-header th,
  .revision-history-grid .k-grid-header .k-cell-inner,
  .revision-history-grid .k-grid-header .k-link,
  .revision-history-grid .k-grid-header .k-column-title {
    background-color: transparent !important;
    color: #64748b !important;
    font-size: 0.95rem !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    cursor: default !important;
    border: none !important;
  }
  .revision-history-grid .k-grid-header th.k-header {
    padding: 0.85rem 1rem !important;
  }
  .revision-history-grid .k-grid-header th.k-header.text-center,
  .revision-history-grid .k-grid-header th.k-header.text-center .k-cell-inner,
  .revision-history-grid .k-grid-header th.k-header.text-center .k-link {
    text-align: center !important;
    justify-content: center !important;
  }
  .revision-history-grid .k-grid-content tr {
    border-bottom: 1px solid #f1f5f9 !important;
  }
  .revision-history-grid .k-grid-content tr,
  .revision-history-grid .k-grid-content tr.k-alt,
  .revision-history-grid .k-grid-content tr.k-table-alt-row {
    background-color: #ffffff !important;
  }
  .revision-history-grid .k-grid-content tr:nth-child(even) {
    background-color: #E8EBEF !important;
  }
  .revision-history-grid .k-grid-content td {
    padding: 0.75rem 1rem !important;
    border-left: none !important;
    border-right: none !important;
  }
`;

export default function RevisionHistoryTable({
  revisions,
}: {
  revisions: Revision[];
}) {
  const sorted = useMemo(
    () =>
      [...revisions].sort((a, b) => {
        const dateA = new Date(a.revisionCreateDate).getTime() || 0;
        const dateB = new Date(b.revisionCreateDate).getTime() || 0;
        return dateB - dateA;
      }),
    [revisions]
  );
  return (
    <div
      className="revision-history-grid rounded-3 overflow-hidden"
      style={{ border: "1px solid #e5e7eb" }}
    >
      <style>{scopedStyles}</style>

      <div
        className="px-4 py-2"
        style={{
          backgroundColor: "#475569",
          color: "#fff",
        }}
      >
        <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
          All Revisions
        </span>
      </div>

      <KendoTableClientState
        data={sorted}
        columns={columns}
        filterable={false}
      />
    </div>
  );
}
